import { defineStore } from 'pinia'
import { type Availability } from '~/classes/availability'
import { type Location } from '~/classes/location'
import {
    OrderDateSelectionType,
    OrderUpdateSource,
    type Order,
    type OrderBasicContactInfo,
    type OrderContactAddress,
    type OrderContactInfo,
    type OrderProduct,
    type OrderStatus,
    type OrderVoucherInfo
} from '~/classes/order'
import { type Product } from '~/classes/product'


export const useBookingStore = defineStore('booking', {
    state: () => ({
        product: null as Product | null,
        locations: [] as Location[],
        availabilities: [] as Availability[],
        order: {
            key: '',
            status: {
                paid: false,
                cancelled: false,
                refunded: false,
                completed: false,
                rescheduled: false
            } as OrderStatus,
            created_at: new Date(),
            updated_at: new Date(),
            update_source: OrderUpdateSource.USER_BOOKING,
            contact_info: {
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                is_business: false,
                business_name: null,
                address: {
                    street: '',
                    house_number: '',
                    house_number_addition: '',
                    postal_code: '',
                    city: '',
                    country: 'Nederland'
                } as OrderContactAddress
            } as OrderContactInfo,
            product: {
                key: ''
            } as OrderProduct,
            location: {
                id: '',
                name: '',
                address: ''
            } as Location,
            amount: 0,
            notes: '',
            date_selection_type: OrderDateSelectionType.IMMEDIATE,
            date: null,
            time_preference: null,
            weights: null,
            emergency_phone: null,
            voucher_info: {
                first_name: '',
                last_name: '',
                message: '',
            } as OrderVoucherInfo,
            voucher_contact_info: null
        } as Order,
        conditions_accepted: false,
        checkinSteps: [] as string[],
    }),
    getters: {
        apiBase(): string {
            const config = useRuntimeConfig()
            return config.public.apiBase
        },
    },
    actions: {
        checkinGetPreviousStep() {
            // Get the current route path
            const currentPath = useRoute().path

            // Find the index of the current step in the checkinSteps array
            const currentIndex = this.checkinSteps.indexOf(currentPath)

            // If the current step is not found or is the first step, return null
            if (currentIndex <= 0 || currentIndex === -1) {
                return null
            }

            // Return the previous step in the checkinSteps array
            return this.checkinSteps[currentIndex - 1]
        },
        checkinGetNextStep() {
            // Get the current route path
            const currentPath = useRoute().path

            // Find the index of the current step in the checkinSteps array
            const currentIndex = this.checkinSteps.indexOf(currentPath)

            // If the current step is the last step, return null
            if (currentIndex >= this.checkinSteps.length - 1) {
                return null
            }

            // If the current step is not found, return the first step
            if (currentIndex === -1) {
                return this.checkinSteps[0]
            }

            // Return the next step in the checkinSteps array
            return this.checkinSteps[currentIndex + 1]
        },
        async getProduct(key: string) {
            try {
                this.product = await $fetch<Product>(`${this.apiBase}product/${key}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
            } catch (err) {
                console.error('Error fetching product:', err)
                this.product = null
            }

            if (this.product) {
                // Fetch locations and availabilities for the product
                await Promise.all(
                    Object.keys(this.product.locations_availability).map(async (locationId) => {
                        await this.getLocation(locationId)
                        await this.getAvailability(this.product!.locations_availability[locationId])
                    })
                )

                // Set the product key in the order
                this.order.product.key = this.product.key

                // Set the amount to the products minimum amount if it is not set
                if (this.order.amount === 0) {
                    this.order.amount = this.product.unit_min || 0
                }

                // Set the selected location to the first available location if not set
                if (!this.order.location.id && this.locations.length > 0) {
                    this.order.location.id = this.locations[0].id
                }
            }
        },
        async getLocation(id: string) {
            let location: Location | null = null
            try {
                location = await $fetch<Location>(`${this.apiBase}location/${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
            } catch (err) {
                console.error('Error fetching location:', err)
                location = null
            }

            if (location) {
                if (!this.locations.some(loc => loc.id === location!.id)) {
                    // Only add the location if it doesn't already exist in the list
                    this.locations.push(location)
                }
            } else {
                // Remove this location from the product's locations_availability if it doesn't exist
                if (this.product && this.product.locations_availability[id]) {
                    delete this.product.locations_availability[id]
                }
            }
        },
        async getAvailability(id: string) {
            let availability: Availability | null = null
            try {
                availability = await $fetch<Availability>(`${this.apiBase}availability/${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
            } catch (err) {
                console.error('Error fetching availability:', err)
                availability = null
            }

            if (availability) {
                if (!this.availabilities.some(avail => avail.id === availability!.id)) {
                    // Only add the availability if it doesn't already exist in the list
                    this.availabilities.push(availability)
                }
            } else {
                // Remove this availability from the product's locations_availability if it doesn't exist
                if (this.product) {
                    for (const loc_id in this.product.locations_availability) {
                        // Check if the availability ID matches the one we tried to fetch
                        if (this.product.locations_availability[loc_id] === id) {
                            delete this.product.locations_availability[loc_id]
                        }
                    }
                }
            }
        },
        async getOrder(key: string) {
            try {
                this.order = await $fetch<Order>(`${this.apiBase}order/${key}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
            } catch (err) {
                console.error('Error fetching order:', err)
                this.order.key = '' // Reset order key if not found
            }

            // Set amount of weights to empty list if not set
            if (!this.order.weights) {
                this.order.weights = []
            }

            // Initialize voucher contact info to empty fields if not set
            if (!this.order.voucher_contact_info && this.order.date_selection_type === OrderDateSelectionType.VOUCHER) {
                console.log('Initializing voucher contact info for order:', this.order)
                this.order.voucher_contact_info = {
                    first_name: this.order.voucher_info?.first_name || '',
                    last_name: this.order.voucher_info?.last_name || '',
                    email: '',
                    phone: ''
                } as OrderBasicContactInfo
            }

            // Convert date to Date object if it is a string
            if (typeof this.order.date === 'string') {
                this.order.date = new Date(this.order.date)
            }

            // Fetch the product for the order if not already set
            if (!this.product || this.product.key !== this.order.product.key) {
                await this.getProduct(this.order.product.key)
            }

            console.log('Order fetched and processed:', this.order)
            console.log('Product for the order:', this.product)

            // Determine necessary check-in steps based on order data
            this.checkinSteps = []
            if (!this.order.date) {
                this.checkinSteps.push('/check-in/date')
            }
            if (!this.order.time_preference && !this.product!.disable_time_preference) {
                this.checkinSteps.push('/check-in/preferred_time')
            }
            if (!this.order.weights || this.order.weights.length === 0 || !this.order.emergency_phone) {
                this.checkinSteps.push('/check-in/weights')
            }
            if (this.order.date_selection_type === OrderDateSelectionType.VOUCHER && (
                !this.order.voucher_contact_info?.first_name ||
                !this.order.voucher_contact_info?.last_name ||
                !this.order.voucher_contact_info?.email ||
                !this.order.voucher_contact_info?.phone
            )) {
                this.checkinSteps.push('/check-in/personal_details')
            }
            this.checkinSteps.push('/check-in/result')

            console.log('Determined check-in steps:', this.checkinSteps)
        },
        async createOrder() {
            // Set time to 0 on the date
            if (this.order.date_selection_type === OrderDateSelectionType.IMMEDIATE && this.order.date) {
                // Convert to UTC keeping local time
                const newTimeUTC = new Date(this.order.date.getTime() - this.order.date.getTimezoneOffset() * 60000)
                this.order.date = newTimeUTC // Update order date
                // Ensure date is stripped of time component
                this.order.date.setUTCHours(0, 0, 0, 0)
            } else {
                this.order.date = null // Clear date for non-immediate bookings
            }

            try {
                const response = await $fetch<Order>(`${this.apiBase}booking/order`, {
                    method: 'POST',
                    body: this.order,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                this.order = response
            } catch (err) {
                console.error('Error creating order:', err)
                throw err
            }
        },
        async updateOrder() {
            // Set time to 0 on the date
            if (this.order.date) {
                // Convert to UTC keeping local time
                const newTimeUTC = new Date(this.order.date.getTime() - this.order.date.getTimezoneOffset() * 60000)
                this.order.date = newTimeUTC // Update order date
                // Ensure date is stripped of time component
                this.order.date.setUTCHours(0, 0, 0, 0)
            }

            try {
                const response = await $fetch<Order>(`${this.apiBase}check-in/order`, {
                    method: 'PUT',
                    body: this.order,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                this.order = response
            } catch (err) {
                console.error('Error updating order:', err)
                throw err
            }
        },
        async createPayment(key: string) {
            try {
                const response = await $fetch<string>(`${this.apiBase}payment/order/${key}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    retry: 0
                })
                return response
            } catch (err) {
                console.error('Error creating payment:', err)
                throw err
            }
        }
    }
})
